import React, { useEffect, useState } from "react";

import { UseAppDispatch } from "redux/store";
import { useSelector } from "react-redux";
import {
  contactInformationSelector,
  IContactInformation,
  setFullContactInformationSlice,
} from "redux/contactInformation";
import { useTranslations } from "hooks/useTranslations";
import { NameLengthNotification } from "../NameLengthNotification/NameLengthNotification";
import {
  useMediaQuery,
  Box,
  Alert,
  Typography,
  Collapse,
  FormLabel,
  Button,
  SxProps,
  useTheme,
} from "@mui/material";
import {
  pushToDataLayer,
  RadioGroup,
  TelInput,
  TextField,
  useConfig,
} from "@thekilroygroup/shared-ui";
import { IAppConfigObject } from "appConfig.interfaces";
import { useForm } from "react-hook-form";
import { domainToCountryMap } from "./contactInformationUtils";

interface IContactInformationProps {
  validateHandler: (valid: boolean) => void;
  handleProceed: () => void;
  initialValidState: boolean;
}

export default function ContactInformation(props: IContactInformationProps) {
  const { handleProceed, validateHandler } = props;
  const { translations } = useTranslations();
  const content = translations.contactInformation;
  const formLabels = translations.formLabels;
  const dispatch = UseAppDispatch();
  const contactInformation = useSelector(contactInformationSelector);
  const { breakpoints } = useTheme();
  const isDesktop = useMediaQuery(breakpoints.up("md"));
  const {
    appConfig: { PRIMARY_LANGUAGE, TOP_LEVEL_DOMAIN },
  } = useConfig<IAppConfigObject>();
  const [nameHasFocus, setNameHasFocus] = useState(false);
  const showNameLengthNotification =
    nameHasFocus && (PRIMARY_LANGUAGE === "nl" || PRIMARY_LANGUAGE === "sv");

  const {
    formWrapper,
    contactFormWrapper,
    alert,
    checkboxWrapper,
    nextStepButtonContainer,
  } = sxStyles;

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    watch,
  } = useForm({
    mode: "onTouched",
  });

  const genderItems = [
    {
      value: "female",
      label: translations.contactInformation.female,
    },
    {
      value: "male",
      label: translations.contactInformation.male,
    },
  ];
  const onProceed = (data: any) => {
    pushToDataLayer("Step 1 - Contact information", "Click next step");
    handleProceed();
  };

  useEffect(() => {
    watch((data) => {
      const values = data as IContactInformation;
      dispatch(setFullContactInformationSlice(values));
    });
  }, [watch]);

  useEffect(() => {
    validateHandler(isValid);
  }, [isValid]);

  return (
    <Box component="form" onSubmit={handleSubmit(onProceed)}>
      <Box sx={formWrapper}>
        <Alert
          severity="info"
          variant={isDesktop ? "neutral" : "grey"}
          icon={false}
          sx={alert}
        >
          <Typography variant="bodyMedium">{content.description}</Typography>
        </Alert>
        <Box
          sx={{
            mb: 4,
            [breakpoints.down("md")]: { mx: 2, mb: 2 },
          }}
        >
          <Collapse in={showNameLengthNotification}>
            <NameLengthNotification
              alertVariant="grey"
              content={translations.travelerInformation.nameLengthNotification}
            />
          </Collapse>
        </Box>
        <Box sx={contactFormWrapper}>
          <TextField
            control={control}
            errors={errors}
            required
            label={formLabels.firstName}
            noSpecialCharacters
            defaultValue={contactInformation.firstName}
            name="firstName"
            formValidationTranslations={translations.formValidations}
            onFocus={() => setNameHasFocus(true)}
            onBlurCapture={() => setNameHasFocus(false)}
          />
          <TextField
            control={control}
            errors={errors}
            noSpecialCharacters
            label={formLabels.middleName}
            defaultValue={contactInformation.middleName}
            name="middleName"
            formValidationTranslations={translations.formValidations}
          />
          <TextField
            control={control}
            errors={errors}
            required
            noSpecialCharacters
            label={formLabels.lastName}
            defaultValue={contactInformation.lastName}
            name="lastName"
            formValidationTranslations={translations.formValidations}
          />
        </Box>
        <Box sx={contactFormWrapper}>
          <TelInput
            control={control}
            errors={errors}
            defaultCountry={
              domainToCountryMap[TOP_LEVEL_DOMAIN ?? ""] ?? undefined
            }
            required
            label={translations.formLabels.phone}
            defaultValue={contactInformation.phoneNumber || undefined}
            name="phoneNumber"
            disableValidation
            disableAutoComplete
            formValidationTranslations={translations.formValidations}
            sx={{
              "& .MuiFormControl-root": {
                width: "100%",
              },
            }}
          />
          <TextField
            control={control}
            errors={errors}
            required
            email
            label={formLabels.email}
            defaultValue={contactInformation.email}
            name="email"
            formValidationTranslations={translations.formValidations}
          />
        </Box>
        <Box sx={checkboxWrapper}>
          <FormLabel>
            <Typography variant="titleMedium" weight="bold">
              {content.gender}
            </Typography>
          </FormLabel>
          <Typography variant="bodySmall">
            {content.genderDescription}
          </Typography>
          <RadioGroup
            control={control}
            errors={errors}
            defaultValue={contactInformation.genderType}
            required
            row
            name="genderType"
            items={genderItems}
            formValidationTranslations={translations.formValidations}
          />
        </Box>
      </Box>
      <Box sx={nextStepButtonContainer}>
        <Button
          color="primary"
          size={isDesktop ? "large" : "xLarge"}
          variant={isDesktop ? "outlined" : "contained"}
          fullWidth={!isDesktop}
          disabled={!isValid}
          disableElevation
          type="submit"
        >
          {translations.navigation.nextStep}
        </Button>
      </Box>
    </Box>
  );
}

const sxStyles: { [key: string]: SxProps } = {
  formWrapper: {
    pb: 2,
    display: "flex",
    flexDirection: "column",
  },
  alert: {
    mb: 2,
    mx: { xs: 2, md: 0 },
    p: { md: 0 },
    order: { xs: 1, md: 0 },
  },
  checkboxWrapper: {
    display: "flex",
    flexDirection: "column",
    p: { xs: 2, md: 0 },
  },
  contactFormWrapper: {
    p: { xs: 2, md: 0 },
    display: "grid",
    gap: 2,
    gridTemplateColumns: {
      xs: "repeat(1, 1fr)",
      sm: "repeat(2, minmax(0, 1fr))",
    },
    mb: 2,
    border: { md: 0 },
  },
  nextStepButtonContainer: {
    pl: 2,
    pr: 2,
    pb: 4,
    display: { md: "flex" },
    padding: { md: 0 },
    justifyContent: { md: "flex-end" },
  },
};
